import {
  FC,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { ApolloError, useMutation } from '@apollo/client';
import {
  AlertIcon,
  Button,
  DateInput,
  H1,
  H2,
  Input,
  Modal,
  Paragraph,
  PhoneInput,
} from '@smartshell/ui-kit';
import { SmartCaptcha } from '@yandex/smart-captcha';
import dayjs from 'dayjs';
import { Controller, useForm } from 'react-hook-form';

import { LocalizationContext } from 'localization';
import { VerifyModal } from 'modals';
import { REGISTER } from 'schemas';
import notification from 'services/notificationService';
import { SettingsContext } from 'settings';
import { EVerificationType, IRegisterRequest, IRegisterResponse } from 'types';

import s from './ClientRegisterModal.module.scss';
import { DEFAULT_VALUES } from './constants';
import { IClientRegisterModalProps, IFormValues } from './interfases';
import { useNotCompleted } from './NotComletedContext';

export const ClientRegisterModal: FC<IClientRegisterModalProps> = ({
  onClose,
  client,
  getClients,
  variables,
}) => {
  const { t, lang } = useContext(LocalizationContext);
  const {
    state: {
      settings: { countryCode, registerWithoutVerification },
    },
  } = useContext(SettingsContext);
  const { addNewData, removeClientById } = useNotCompleted() as NonNullable<
    ReturnType<typeof useNotCompleted>
  >;

  const [verification, setVerification] = useState<
    IRegisterResponse['register']['verification'] | null
  >(null);
  const [verificationType, setVerificationType] =
    useState<EVerificationType | null>(
      !registerWithoutVerification ? EVerificationType.OUTGOING_CALL : null
    );
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [captchaKey, setCaptchaKey] = useState(0);
  const [verifyClientId, setVerifyClientId] = useState<number | null>(null);
  const [notCompleted, setNotCompleted] = useState<boolean>(false);
  const [onSuccess, setOnSuccess] = useState<boolean>(false);
  const [notValidMethod, setNotValidMethod] = useState<Set<EVerificationType>>(
    new Set()
  );
  const [userPhone, setUserPhone] = useState(client?.phone || '');

  const { control, handleSubmit, watch, setValue, setError } =
    useForm<IFormValues>({ mode: 'onChange', defaultValues: DEFAULT_VALUES });
  const phone = watch('phone');
  const email = watch('email');
  const dob = watch('dob');
  const nickname = watch('nickname');

  const setNotValidMethodHandler = useCallback(
    (method: EVerificationType) => {
      setNotValidMethod((prev) => {
        const newSet: Set<EVerificationType> = new Set(prev);
        newSet.add(method);
        return newSet;
      });
    },
    [notValidMethod, setNotValidMethod]
  );

  useEffect(() => {
    if (verification) return;
    setCaptchaKey((prev) => prev + 1);
  }, [verification]);

  useEffect(() => {
    if (client) {
      setValue('nickname', client.nickname);
      setValue('first_name', client.first_name);
      setValue('dob', client.dob);
      setValue('phone', client.phone);
      setValue('email', client.email);
    }
  }, []);

  const isUnder15Years = (dob: string): boolean => {
    const today = dayjs();
    const birthDate = dayjs(dob, 'DD.MM.YYYY');
    return today.diff(birthDate, 'year') < 15;
  };

  const handleAddNewData = (): void => {
    const formData = watch();
    const dataToSave = {
      ...formData,
      captcha_token: captchaToken,
      createdAt: Date.now(),
      id: Date.now(),
      password: phone,
      verifyClientId: verifyClientId,
    };
    addNewData(dataToSave);
  };

  const [register, { loading }] = useMutation<
    IRegisterResponse,
    IRegisterRequest
  >(REGISTER, {
    onCompleted: ({ register }) => {
      if (registerWithoutVerification) {
        return setOnSuccess(true);
      }

      if (!register.verification.success) throw new Error('Ошибка сервера');

      setVerification({ ...register.verification });
      setVerifyClientId(register.user.id);
      if (register.user.phone !== userPhone) {
        setNotValidMethod(new Set());
        setUserPhone(register.user.phone);
      }
    },
    onError: (err: ApolloError) => {
      setCaptchaKey((prev) => prev + 1);
      setCaptchaToken(null);

      if (err.message.includes('phone')) {
        setError('phone', { message: t('clientRegister.modal.phone.error') });
      }
      if (err.message.includes('years')) {
        setError('dob', { message: t('clientRegister.modal.dob.error') });
      }
      if (err.message.includes('email')) {
        setError('email', { message: t('clientRegister.modal.email.error') });
      }
      if (err.message.includes('length')) {
        setError('nickname', {
          message: t('clientRegister.modal.login.error'),
        });
      }
      if (err.graphQLErrors && err.graphQLErrors.length > 0) {
        err.graphQLErrors.forEach((graphQLError) => {
          if (graphQLError.message.includes('date')) {
            setError('dob', { message: t('clientRegister.modal.dob2.error') });
          }
        });
      }
      notification.info({
        t,
        title: t('server.error'),
        type: 'error',
        dismiss: 5,
      });
    },
  });

  useEffect(() => {
    if (verifyClientId) {
      handleAddNewData();
    }
  }, [verifyClientId]);

  const submitDisabled = useMemo(
    () => !phone || !nickname || !dob || (countryCode !== 'RU' && !email),
    [phone, email, nickname, dob]
  );

  if (verification && !!verificationType) {
    return (
      <VerifyModal
        onClose={() => {
          setVerification(null);
          setNotCompleted(true);
        }}
        phone={phone}
        email={email}
        verifyClientId={verifyClientId}
        onPhoneChange={() => {
          setVerification(null);
          setVerificationType(EVerificationType.OUTGOING_CALL);
        }}
        setOnSuccess={() => setOnSuccess(true)}
        setVerification={setVerification}
        getClients={getClients}
        variables={variables}
        verification={verification}
        verificationType={verificationType}
        setVerificationType={setVerificationType}
        notValidMethod={notValidMethod}
        setNotValidMethod={setNotValidMethodHandler}
      />
    );
  }

  if (notCompleted) {
    return (
      <Modal className={s.notCompleted} disableOutsideClose>
        <H1>{t('clientRegister.modal.onCompleted.title')}</H1>
        <p className={s.notCompleted_text}>
          {t('clientRegister.modal.onCompleted.description')}
        </p>
        <Button
          className={s.button}
          fullWidth
          onClick={() => {
            onClose && onClose();
            setNotCompleted(false);
          }}
        >
          {t('next.later')}
        </Button>
      </Modal>
    );
  }

  if (onSuccess) {
    return (
      <Modal className={s.onSuccess} disableOutsideClose>
        <AlertIcon className={s.onSuccess__icon} color="warning" />
        <div className={s.header}>
          <H2>{t('clientRegister.modal.onSuccess.title')}</H2>
          <Paragraph className={s.onSuccess_text} bold color="secondary">
            {t('clientRegister.modal.onSuccess.text', phone)}
          </Paragraph>
        </div>
        <Button
          fullWidth
          onClick={() => {
            onClose && onClose();
            setOnSuccess(false);
            removeClientById(verifyClientId as number);
          }}
        >
          {t('well')}
        </Button>
      </Modal>
    );
  }

  return (
    <Modal className={s.modal} onClose={onClose} disableOutsideClose>
      <form
        className={s.form}
        onSubmit={handleSubmit(
          ({
            // first_name,
            nickname,
            dob,
            phone,
            email,
          }) => {
            if (loading || (countryCode === 'ru' && !captchaToken)) return;

            if (captchaToken) {
              return register({
                variables: {
                  input: {
                    // first_name,
                    nickname: nickname.trim(),
                    dob: dayjs(dob, 'DD.MM.YYYY').format('YYYY-MM-DD'),
                    phone,
                    password: phone,
                    confirm_password: phone,
                    email: email.length ? email : undefined,
                    captcha_token: captchaToken,
                    verification_type: verificationType,
                    news_consent: false,
                  },
                },
              });
            }
          }
        )}
      >
        <div className={s.header}>
          <H2>{t('clientRegister.modal.title')}</H2>
          <p className={s.text}>{t('clientRegister.modal.password.info')}</p>
        </div>
        <div className={s.form_data}>
          <Controller
            render={({ field, fieldState: { error } }) => (
              <DateInput
                {...field}
                title={t('birth.date')}
                error={error?.message}
                autoComplete="off"
              />
            )}
            name={'dob'}
            control={control}
            rules={{
              required: t('errors.required'),
              pattern: {
                value: /^(0[1-9]|[12][0-9]|3[01])\.(0[1-9]|1[0-2])\.\d{4}$/,
                message: t('clientRegister.modal.dob2.error'),
              },
              validate: (value) =>
                !isUnder15Years(value) || t('clientRegister.modal.dob.error'),
            }}
          />

          <Controller
            render={({ field, fieldState: { error } }) => (
              <PhoneInput
                {...field}
                title={t('phone')}
                error={error?.message}
                type="number"
                autoComplete="off"
              />
            )}
            name={'phone'}
            control={control}
            rules={{
              required: t('errors.required'),
              minLength: {
                value: 11,
                message: t('clientRegister.modal.phone2.error'),
              },
            }}
          />

          <Controller
            render={({ field, fieldState: { error } }) => (
              <Input
                {...field}
                title={t('client.nickname')}
                error={error?.message}
                autoComplete="off"
              />
            )}
            name="nickname"
            control={control}
            rules={{
              required: t('errors.required'),
              pattern: {
                value: /^\S.*$/,
                message: t('clientRegister.modal.login2.error'),
              },
              minLength: {
                value: 5,
                message: t('clientRegister.modal.login.error'),
              },
              maxLength: {
                value: 30,
                message: t('clientRegister.modal.login.error'),
              },
            }}
          />

          <Controller
            render={({ field, fieldState: { error } }) => (
              <Input
                {...field}
                title={
                  countryCode === 'RU'
                    ? t('clientRegister.modal.email')
                    : t('email')
                }
                error={error?.message}
                autoComplete="off"
              />
            )}
            name={'email'}
            control={control}
            rules={{
              required: countryCode !== 'RU' ? t('errors.required') : undefined,
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9-]+.+.[A-Z]{2,4}$/i,
                message: t('errors.email.valid'),
              },
            }}
          />

          <Controller
            render={({ field, fieldState: { error } }) => (
              <Input
                {...field}
                title={t('clientRegister.modal.name')}
                error={error?.message}
                autoComplete="off"
              />
            )}
            name="first_name"
            control={control}
            rules={{
              maxLength: {
                value: 30,
                message: t('errors.maxLength', 30),
              },
            }}
          />
        </div>

        {process.env.REACT_APP_CAPTCHA_KEY && (
          <div className={s.capcha}>
            <SmartCaptcha
              key={captchaKey}
              sitekey={process.env.REACT_APP_CAPTCHA_KEY}
              language={lang === 'ru' ? 'ru' : 'en'}
              onSuccess={setCaptchaToken}
              onTokenExpired={() => setCaptchaToken(null)}
            />
          </div>
        )}
        <Button
          isLoading={loading}
          disabled={submitDisabled || !captchaToken}
          type="submit"
          fullWidth
        >
          {t('next')}
        </Button>
      </form>
    </Modal>
  );
};
